import { useState, useEffect, useRef } from "react";
import styles from "./header.module.css";
import { Logo } from "../../../../constants";
import {
  IconArrowRight,
  IconBell,
  IconCheck,
  IconMenu2,
  IconMessageCircle,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { getNotifications, markNotificationAsRead } from "../../../../services/talentCalls";
import { searchAdvance } from "../../../../services/clientCalls";
import { errorHandler } from "../../../../utils";
import Notifications from "../../../NotificationDropdown/notificationDropdown";

const formatTalentNotification = (notification) => {
  const { type, title } = notification;
  switch (type) {
    case "JOB_ALERT": {
      const jobTitle = title?.replace("New Job Alert: ", "") || "Job";
      const jobId = notification?.metadata?.jobId;
      return {
        message: `Hola! A new ${jobTitle} job with your skills just got posted.`,
        actionUrl: jobId ? `/job/${jobId}` : "/job-feed",
      };
    }
    case "INTERVIEW_SCHEDULED": {
      const jobId = notification?.metadata?.jobId;
      return {
        message: "You have a new interview scheduled.",
        actionUrl: jobId ? `/job/${jobId}` : "/matches",
      };
    }
    case "JOB_INVITE": {
      const jobId = notification?.metadata?.jobId;
      return {
        message: "You've been invited to apply for a job.",
        actionUrl: jobId ? `/job/${jobId}` : "/job-feed",
      };
    }
    case "MESSAGE":
      return { message: "You've received a new message.", actionUrl: "/talent-messages" };
    case "VERIFICATION_APPROVED":
      return { message: "Your profile verification has been approved.", actionUrl: "/talent-profile" };
    case "ONBOARDING_REMINDER":
      return { message: "Hello, just a reminder that you haven't completed onboarding.", actionUrl: "/talent-onboarding?stage=1" };
    case "ONBOARDING_COMPLETED":
      return { message: "Congratulations, you just completed your onboarding! Thank you.", actionUrl: "/talent-dashboard" };
    case "PROFILE_UPDATED":
      return { message: "You have just updated your profile.", actionUrl: "/talent-profile" };
    case "VERIFICATION_SUCCESS":
      return { message: "Nice! You just got verified! You are now ready to start applying for jobs.", actionUrl: "/job-feed" };
    case "APPLICATION_ACCEPTED": {
      const jobId = notification?.metadata?.jobId;
      return {
        message: notification?.message?.replace(/<[^>]*>/g, "") || "A client just accepted your application to their job role. Congratulations!",
        actionUrl: jobId ? `/job/${jobId}` : "/matches",
      };
    }
    case "SKILL_DEVELOPMENT":
      return { message: "Hola, you really need to keep stepping up your game. Check out our recommended self development resources.", actionUrl: "/self-development" };
    case "SUPPORT_REPLY":
      return {
        message: notification?.message?.replace(/<[^>]*>/g, "") || "The admin team has replied to your support request.",
        actionUrl: "/talent-messages?tab=support",
      };
    default:
      return {
        message: notification?.message?.replace(/<[^>]*>/g, "") || title?.replace(/<[^>]*>/g, "") || "You have a new notification.",
        actionUrl: "/talent-dashboard",
      };
  }
};

const Header = ({ sidebarMobileOpen, sidebarCollapsed, mobileSearchOpen, onSidebarToggle, onMobileSearchToggle, onMobileSearchClose, data }) => {
  const navigate = useNavigate();
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 730);
  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const searchWrapRef = useRef(null);

  const [mobileSearchValue, setMobileSearchValue] = useState("");
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false);
  const [mobileSuggestions, setMobileSuggestions] = useState([]);
  const mobileSearchInputRef = useRef(null);

  useEffect(() => {
    const handler = () => {
      setIsMobileView(window.innerWidth <= 768);
      setIsMobile(window.innerWidth <= 730);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (mobileSearchOpen && mobileSearchInputRef.current) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 50);
    }
  }, [mobileSearchOpen]);

  const fetchSuggestions = async (query) => {
    try {
      const res = await searchAdvance(query, ["title"]);
      const jobs = res?.data?.data?.data || [];
      const uniqueTitles = [...new Set(jobs?.map((j) => j?.title).filter(Boolean))];
      return uniqueTitles.slice(0, 8);
    } catch (_) {
      return [];
    }
  };

  useEffect(() => {
    const query = searchValue.trim();
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    let active = true;
    const timer = setTimeout(async () => {
      const results = await fetchSuggestions(query);
      if (active) setSuggestions(results);
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [searchValue]);

  useEffect(() => {
    const query = mobileSearchValue.trim();
    if (query.length < 2) {
      setMobileSuggestions([]);
      return;
    }
    let active = true;
    const timer = setTimeout(async () => {
      const results = await fetchSuggestions(query);
      if (active) setMobileSuggestions(results);
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [mobileSearchValue]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goToSearch = (query) => {
    if (query) navigate(`/search?query=${encodeURIComponent(query)}&field=title&category=Role`);
  };

  const ToggleIcon = isMobileView ? (sidebarMobileOpen ? IconX : IconMenu2) : sidebarCollapsed ? IconMenu2 : IconX;

  const [notifOpen, setNotifOpen] = useState(false);
  const [rawNotifData, setRawNotifData] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [seenAt, setSeenAt] = useState(() => {
    const stored = localStorage.getItem("notificationsSeenAt");
    return stored ? parseInt(stored, 10) : Date.now();
  });

  const updateCounts = (notifs, baseline) => {
    const unread = notifs?.filter((n) => !n?.isRead);
    const newlyArrived = notifs?.filter((n) => !n?.isRead && new Date(n?.createdAt).getTime() > baseline);
    setUnreadCount(unread?.length || 0);
    setNewCount(newlyArrived?.length || 0);
    setHasUnread(unread?.length > 0 || newlyArrived?.length > 0);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getNotifications();
        const notifs = res?.data?.data?.notifications ?? [];
        setRawNotifData(notifs);
        updateCounts(notifs, seenAt);
      } catch (err) {
        errorHandler(err);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBellClick = () => {
    const now = Date.now();
    setSeenAt(now);
    localStorage.setItem("notificationsSeenAt", now.toString());

    if (isMobile) {
      setHasUnread(false);
      setUnreadCount(0);
      setNewCount(0);
      navigate("/talent-activity-summary?onlyNotifications=true");
    } else {
      setNotifOpen((prev) => {
        if (!prev) {
          setHasUnread(false);
          setUnreadCount(0);
          setNewCount(0);
        }
        return !prev;
      });
    }
  };

  const handleNotifClick = async (id) => {
    setRawNotifData((prev) => prev?.map((n) => (n?._id === id ? { ...n, isRead: true } : n)));
    try {
      await markNotificationAsRead(id);
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    const unread = rawNotifData?.filter((n) => !n?.isRead) || [];
    setRawNotifData((prev) => prev?.map((n) => ({ ...n, isRead: true })));
    setHasUnread(false);
    setUnreadCount(0);
    setNewCount(0);
    try {
      await Promise.allSettled(unread.map((n) => markNotificationAsRead(n._id)));
    } catch (_) {}
  };

  const handleSeeAll = () => {
    setNotifOpen(false);
    navigate("/talent-dashboard");
  };

  const processedNotifs = rawNotifData?.map((n) => ({ ...n, ...formatTalentNotification(n) })) || [];

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setShowSuggestions(false);
      goToSearch(e.target.value.trim());
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const avatarLetter = data?.firstName?.charAt(0)?.toUpperCase() || "T";

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <button className={styles.topbarToggle} onClick={onSidebarToggle} aria-label="Toggle sidebar">
            <ToggleIcon size={20} />
          </button>
          <span className={styles.topbarLogo}>
            <Logo color="#030659" />
          </span>
        </div>

        <div className={styles.topbarRight}>
          <div className={styles.topbarSearch} ref={searchWrapRef}>
            <IconSearch size={18} />
            <input
              type="text"
              placeholder="Search for Jobs"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
            />
            {showSuggestions && suggestions?.length > 0 && (
              <ul className={styles.searchSuggestions}>
                {suggestions.map((title, i) => (
                  <li key={`${title}-${i}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSearchValue(title);
                        setShowSuggestions(false);
                        goToSearch(title);
                      }}
                    >
                      <IconSearch size={14} />
                      <span>{title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.topbarActions}>
            <button id="mobileSearchBtn" className={`${styles.topbarIconBtn} ${styles.mobileSearchBtn}`} aria-label="Search" onClick={onMobileSearchToggle}>
              <IconSearch size={18} />
            </button>

            <Link to="/talent-messages" className={styles.topbarIconBtn} aria-label="Messages">
              <IconMessageCircle size={18} />
            </Link>

            <div className={styles.notifWrap}>
              <button
                className={styles.topbarIconBtn}
                aria-label="Notifications"
                onClick={handleBellClick}
                data-unread-count={`${unreadCount + newCount} unread`}
              >
                <IconBell size={18} />
                {hasUnread && <span className={styles.notificationDot} />}
              </button>

              <Notifications
                notifications={processedNotifs}
                isOpen={notifOpen}
                onClose={() => setNotifOpen(false)}
                onMarkAllRead={handleMarkAllRead}
                onNotifClick={handleNotifClick}
                onSeeAll={handleSeeAll}
              />
            </div>

            <Link to="/talent-profile" className={styles.avatar}>
              {avatarLetter}
            </Link>

            <span className={styles.topbarName} onClick={() => navigate("/talent-profile")}>
              {data?.firstName} {data?.lastName}
            </span>

            {data?.profileVerified && (
              <span className={styles.verifiedBadge} title="Verified profile" aria-label="Verified profile">
                <IconCheck size={10} stroke={3} />
              </span>
            )}
          </div>
        </div>
      </header>

      <div id="mobileSearchPanel" className={`${styles.mobileSearchPanel} ${mobileSearchOpen ? styles.mobileSearchPanelOpen : ""}`}>
        <div className={styles.mobileSearchInputWrap}>
          <IconSearch size={16} className={styles.searchPrefixIcon} />
          <input
            ref={mobileSearchInputRef}
            type="search"
            placeholder="Search for Jobs"
            autoComplete="off"
            value={mobileSearchValue}
            onChange={(e) => {
              setMobileSearchValue(e.target.value);
              setShowMobileSuggestions(true);
            }}
            onFocus={() => setShowMobileSuggestions(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const query = e.target.value.trim();
                setShowMobileSuggestions(false);
                onMobileSearchClose();
                goToSearch(query);
              } else if (e.key === "Escape") {
                setShowMobileSuggestions(false);
                onMobileSearchClose();
              }
            }}
          />
          <button className={styles.mobileSearchSubmit} aria-label="Submit search" onClick={onMobileSearchClose}>
            <IconArrowRight size={14} />
          </button>
          {showMobileSuggestions && mobileSuggestions?.length > 0 && (
            <ul className={styles.searchSuggestions}>
              {mobileSuggestions.map((title, i) => (
                <li key={`${title}-${i}`}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setMobileSearchValue(title);
                      setShowMobileSuggestions(false);
                      onMobileSearchClose();
                      goToSearch(title);
                    }}
                  >
                    <IconSearch size={14} />
                    <span>{title}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button className={styles.mobileSearchCancel} onClick={onMobileSearchClose}>
          Cancel
        </button>
      </div>
    </>
  );
};

export default Header;
